/* eslint-disable no-nested-ternary */
import React, {
  useState, useCallback, useMemo, useEffect, useLayoutEffect, useRef,
} from 'react';
import { createPortal } from 'react-dom';
import cn from 'classnames';
import {
  getAssetFolderList,
  getAssetList,
  getMediaLibraryFolderTree,
  saveMediaLibraryItem,
  deleteAsset,
  starAsset,
  batchActionAsset,
  downloadAssetFolder,
  getAssetFolderDownloadStatus,
  isAssetLibraryApiMessageShown,
  pinAssetFolder,
} from '$/api/canvasRquest';
import type {
  AssetFolderDownloadStatusResponse,
  AssetItem,
  AssetListParams,
  MediaLibraryItemSaveParams,
} from '$/api/server.types';
import IconFont from '$/components/IconFont';
import { message } from '$/components/Message';
import DownloadPreferencePopup from '$/components/DownloadPreferencePopup';
import { downloadFileFromUrl } from '$/utils/export';
import useThemeStore from '$/store/useThemeStore';
import { useOfficialStore } from '$/store/useOfficialStore';
import { useLanguageStore } from '$/store/languageStore';
import { openPreview } from '$/store/usePreviewStore';
import { getTeamMembers } from '@/domains/team/teamService';
import type { TeamMemberListItem } from '@/domains/team/types';
import { useTeamPermissions } from '@/domains/team/useTeamPermissions';
import {
  ASSET_LIST_TASK_TYPE_FILTER_OPTIONS,
  type AssetListTaskTypeFilterValue,
} from '$/constants/assetListTaskTypeFilter';
import {
  ASSET_AUDIO_CARD_DISPLAY_PX,
  ASSET_MODAL_MASONRY_CARD_WIDTH,
  assetDisplayTitle,
  documentVisualVariantKey,
  formatAssetByteSizeMeta,
  getDocumentCardVisual,
  isDocumentCardLayout,
  masonryAudioItemHeightShare,
  masonryColumnCountForFixedCardWidth,
} from '$/utils/assetLibraryCardLayout';
import type { WorkflowCanvasMediaItem } from '$/utils/openWorkflowWithMedia';
import darkVoiceArt from '../folderAsset/dark-voice.svg';
import lightVoiceArt from '../folderAsset/light-voice.svg';
import styles from '../index.module.scss';
import type {
  MaterialTypeValue, AssetItemView, AssetGroupView, HistoryModalContentProps,
} from '../type';
import {
  MATERIAL_OPTION_DEFS,
  staticImportedSrc,
  sortFoldersForDropdown,
  isCanvasInsertableMaterialType,
  isWorkflowInsertableMaterialType,
  isBuiltInModalBatchSelectableMaterialType,
  shouldShowAssetInsertHoverBadge,
  buildAssetModalGroupsFromList,
  flattenMediaLibraryFolderTree,
  mapAssetRowToModalViewItem,
  trackRoboneoAssetUploadSuccess,
  trackRoboneoHistoryMedia,
  trackRoboneoAssetLibraryShow,
  trackRoboneoAssetLibraryCardShow,
  trackRoboneoAssetAddCanvasClick,
  assetItemToWorkflowMediaItem,
  insertAssetItemToCanvas,
  resolveRoomIdByInsertTarget,
} from '../utils';
import {
  trackRoboneoHistoryBatchBarShow,
  trackRoboneoHistoryCollectAction,
  trackRoboneoHistoryContentDelete,
  trackTeamAssetHistorySave,
  trackTeamHistoryCardClick,
  trackTeamHistoryCardShow,
} from '../tracking';
import {
  AssetModalItemRoot,
  AssetModalConfirmDialog,
  AssetModalMediaCover,
  AssetModalMoveDialog,
  AssetPreviewAddToCanvasSlot,
  type AssetPreviewSaveLocation,
  DEFAULT_PAGE_SIZE,
  type FolderItem,
  MEDIA_LIBRARY_HISTORY_UPLOAD_LIMITS,
  type MediaLibraryUploadMaterialType,
  buildAssetDocumentTextPreviewItem,
  buildAssetMediaPreviewList,
  buildMediaLibrarySaveParamsFromHistoryItem,
  getAssetWatermarkUrl,
  getAssetModalDownloadFilename,
  isAssetCardClickPreviewable,
  isAssetImagePreviewable,
  isAssetVideoPreviewable,
  resolveAssetViewMediaLibraryMaterialType,
} from '../shared/AssetModalShared';
import SubjectPickerBatchFooter from '../shared/SubjectPickerBatchFooter';
import {
  canToggleBatchAssetSelection,
  normalizeMaxSelectableCount,
  toggleBatchAssetSelection,
} from '../shared/batchSelection';

const HISTORY_ASSET_STACKED_ACTIONS_MIN_HEIGHT_PX = 68;
const HISTORY_ASSET_DOWNLOAD_POLL_INTERVAL_MS = 2000;
const HISTORY_ASSET_DOWNLOAD_POLL_MAX_ATTEMPTS = 150;
type HistoryDeleteConfirmState =
  | {
    kind: 'historyItem';
    item: AssetItemView;
  }
  | {
    kind: 'historyBatch';
    assetIds: number[];
  }
  | null;

/**
 * 获取团队成员的可读名称，昵称为空时依次回退手机号、邮箱和 UID。
 */
function getTeamMemberDisplayName(member: TeamMemberListItem): string {
  return member.screen_name?.trim()
    || member.phone?.trim()
    || member.email?.trim()
    || String(member.uid);
}

/**
 * 渲染团队成员头像；头像不可用时保留与设计稿一致的圆形渐变占位。
 */
function TeamMemberAvatar({ member }: { member: TeamMemberListItem; }): React.ReactElement {
  const [loadFailed, setLoadFailed] = useState(false);

  useEffect(() => {
    setLoadFailed(false);
  }, [member.avatar]);

  if (!member.avatar || loadFailed) {
    return <span className={styles.teamMemberAvatar} aria-hidden />;
  }

  return (
    <span className={styles.teamMemberAvatar} aria-hidden>
      {/* eslint-disable-next-line @next/next/no-img-element */}
      <img
        className={styles.teamMemberAvatarImage}
        src={member.avatar}
        alt=""
        crossOrigin="anonymous"
        onError={() => setLoadFailed(true)}
      />
    </span>
  );
}

/**
 * 从弹窗视图项中读取历史记录 asset_id，避免误用资产库 item_id。
 */
function getHistoryAssetNumericId(item?: AssetItemView | null): number | undefined {
  const id = Number(item?.raw.id ?? item?.id);
  return Number.isFinite(id) && id > 0 ? id : undefined;
}
/**
 * 轮询历史记录批量打包任务，终态或超时后交由调用方处理下载与提示。
 */
async function waitForHistoryAssetBatchDownloadTask(
  taskId: string,
  attempt = 0,
): Promise<AssetFolderDownloadStatusResponse | null> {
  if (attempt >= HISTORY_ASSET_DOWNLOAD_POLL_MAX_ATTEMPTS) return null;
  const result = await getAssetFolderDownloadStatus(taskId);
  if (result.status === 'success' || result.status === 'failed' || result.error_msg) {
    return result;
  }
  await new Promise<void>((resolve) => {
    setTimeout(resolve, HISTORY_ASSET_DOWNLOAD_POLL_INTERVAL_MS);
  });
  return waitForHistoryAssetBatchDownloadTask(taskId, attempt + 1);
}
/**
 * Figma 33703:123959：文档类素材卡主体（圆角底色图标 + 文件名 + 类型/大小）。
 * Figma 33766:107595：副行為「文件類型 · 大小」結構化展示，與資產頁瀑布流一致。
 *
 * @param props.item 列表视图项
 */
function AssetDocumentCardBody(props: { item: AssetItemView; }): React.ReactElement {
  const { item } = props;
  const visual = getDocumentCardVisual(documentVisualVariantKey(item.cardVariant, item.materialType));
  const title = assetDisplayTitle(item.raw, item.mediaUrl, item.id);
  const sizePart = formatAssetByteSizeMeta(item.raw);
  const metaTitle = sizePart ? `${visual.typeLabel} · ${sizePart}` : visual.typeLabel;
  return (
    <div className={styles.assetDocLayout}>
      <div className={styles.assetDocIconBadge} style={{ background: visual.iconBg }} aria-hidden>
        <IconFont name={visual.icon} fontSize={16} color="#fff" />
      </div>
      <div className={styles.assetDocTexts}>
        <div className={styles.assetDocTitle} title={title}>
          {title}
        </div>
        <div className={styles.assetDocMeta} title={metaTitle}>
          <span className={styles.assetDocMetaType}>{visual.typeLabel}</span>
          {sizePart ? (
            <>
              <span className={styles.assetDocMetaSep} aria-hidden>
                {' '}
                ·
                {' '}
              </span>
              <span className={styles.assetDocMetaSize}>{sizePart}</span>
            </>
          ) : null}
        </div>
      </div>
    </div>
  );
}
/**
 * 圖 / 視頻 / 音頻卡右上角格式後綴（與資產頁 MasonryGrid `assetMediaSuffixBadge`、Figma 31350:122419 一致）。
 *
 * @param props.item 列表视图项
 */
function AssetMediaExtensionBadge(props: { item: AssetItemView; }): React.ReactElement | null {
  const { item } = props;
  if (!['image', 'video', 'audio'].includes(item.cardVariant)) {
    return null;
  }
  const extShow = (item.fileExtension || '').trim().toUpperCase();
  if (!extShow) return null;
  return (
    <div className={styles.assetMediaExtBadge} aria-hidden>
      <span className={styles.assetMediaExtText}>{extShow}</span>
    </div>
  );
}
/** 历史记录弹窗主体，兼容默认管理态与外部托管的资源选择态。 */
function HistoryModalContent({
  setOpenAssetPopover,
  pickerSource,
  maxSelectableCount,
  insertTarget = 'canvas',
  trackLocation,
  enableUploadSuccessTrack = true,
  enableAddCanvasClickTrack = true,
  closeOnBatchConfirm = true,
  defaultBatchMode = false,
  enableAddToAssetLibrary = false,
  hideBatchManagementActions = false,
  canvasProjectId,
  onInsertWorkflowMedia,
  onAssetItemClick,
  disableAssetItemClick = false,
  onAssetBatchItemClick,
  onAssetBatchConfirm,
  batchConfirmLabel,
  assetActionLabel,
  allowedMaterialTypes,
}: HistoryModalContentProps) {
  /**
   * 画布 / Agent 团队 / 工作流等内置插入：素材类型筛选与资产页一致展示全部枚举；批量勾选仍仅限可插入类型。
   * 父组件托管点击/批量（如团队对话框）时不启用。
   */
  const limitBuiltInBatchMedia = !onAssetItemClick
    && !onAssetBatchConfirm
    && (insertTarget === 'canvas' || insertTarget === 'agentTeam' || insertTarget === 'workflow');
  // 只有主体库来源启用外部选择上限，普通历史记录继续沿用原批量处理能力。
  const subjectPickerMaxSelectableCount = pickerSource === 'subjectLibrary'
    ? normalizeMaxSelectableCount(maxSelectableCount)
    : undefined;
  const isSubjectLibraryPicker = pickerSource === 'subjectLibrary'
    && subjectPickerMaxSelectableCount != null;
  const languageStore = useLanguageStore((s) => s.languageStore);
  const theme = useThemeStore((s) => s.theme);
  const teamOid = useOfficialStore((s) => s.clientOId);
  const navigateMap = useOfficialStore((s) => s.navigateMap);
  const currentUserUid = useOfficialStore((s) => s.userInfo.uid);
  const isTeamHistory = teamOid != null && teamOid > 0;
  const {
    canDelete,
    canDownload,
  } = useTeamPermissions();
  const permissionDeniedText = languageStore.smartcatTranslationText(
    (value) => value.you_currently_have_no_operation_permissions_please_contact_the_administrator_for_an_update_,
  );
  const filterByMemberText = languageStore.smartcatTranslationText(
    (value) => value.filter_by_member,
  );
  const teamHistoryTitleText = languageStore.smartcatTranslationText(
    (value) => value.team_history_record,
  );
  const teamMembersText = languageStore.smartcatTranslationText(
    (value) => value.team_members,
  );
  const noTeamMembersText = languageStore.smartcatTranslationText(
    (value) => value.no_team_members_available,
  );
  const currentUserSuffixText = languageStore.smartcatTranslationText((value) => value._i_);
  const unnamedFolderText = languageStore.smartcatTranslationText((value) => value.unnamed_folder);
  const generatedCharacterText = languageStore.smartcatTranslationText(
    (value) => value.generate_character,
  );
  const canvasFallback = languageStore.smartcatTranslationText((value) => value.roboneo_web_canvas);
  const voiceArtSrc = useMemo(() => staticImportedSrc(theme === 'light' ? lightVoiceArt : darkVoiceArt), [theme]);
  const materialOptionsTranslated = useMemo(() => {
    const labelForMaterialOption = (value: MaterialTypeValue): string => {
      switch (value) {
        case 'all':
          return languageStore.smartcatTranslationText((v) => v.all);
        case 'image':
          return languageStore.smartcatTranslationText((v) => v.pictures);
        case 'video':
          return languageStore.smartcatTranslationText((v) => v.roboneo_web_video);
        case 'audio':
          return languageStore.smartcatTranslationText((v) => v.audio);
        case 'document':
          return languageStore.smartcatTranslationText((v) => v.asset_type_files);
        case 'excel':
          return 'Excel';
        case 'ppt':
          return languageStore.smartcatTranslationText((v) => v['333']);
        default:
          return value;
      }
    };
    return MATERIAL_OPTION_DEFS.map((o) => ({ ...o, label: labelForMaterialOption(o.value) }));
  }, [languageStore]);
  const allowedMaterialTypeValues = useMemo(() => {
    return Array.from(new Set(allowedMaterialTypes?.filter((value) => value !== 'all') ?? []));
  }, [allowedMaterialTypes]);
  const materialOptionsVisible = useMemo(() => {
    if (allowedMaterialTypeValues.length === 0) {
      return materialOptionsTranslated;
    }
    return materialOptionsTranslated.filter((opt) => allowedMaterialTypeValues.includes(opt.value));
  }, [allowedMaterialTypeValues, materialOptionsTranslated]);
  /** 与 `ASSET_LIST_TASK_TYPE_FILTER_OPTIONS.labelKey` 一致，统一走 smartcat 文案 */
  const getTaskTypeFilterLabel = useCallback(
    (filterValue: AssetListTaskTypeFilterValue) => {
      const opt = ASSET_LIST_TASK_TYPE_FILTER_OPTIONS.find((o) => o.value === filterValue);
      if (!opt) return '';
      return languageStore.smartcatTranslationText((v) => v[opt.labelKey]);
    },
    [languageStore],
  );
  const [folders, setFolders] = useState<FolderItem[]>([]);
  const [selectedFolderId, setSelectedFolderId] = useState<number | undefined>(undefined);
  const [taskType, setTaskType] = useState<AssetListTaskTypeFilterValue>('all');
  const [materialType, setMaterialType] = useState<MaterialTypeValue>(allowedMaterialTypeValues[0] ?? 'all');
  const [favoriteChecked, setFavoriteChecked] = useState(false);
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  type ListGroupMode = 'time' | 'room';
  const [listGroupMode, setListGroupMode] = useState<ListGroupMode>('time');
  const [viewModeDropdownOpen, setViewModeDropdownOpen] = useState(false);
  const [groups, setGroups] = useState<AssetGroupView[]>([]);
  const [loading, setLoading] = useState(false);
  const [folderDropdownOpen, setFolderDropdownOpen] = useState(false);
  const [taskDropdownOpen, setTaskDropdownOpen] = useState(false);
  const [materialDropdownOpen, setMaterialDropdownOpen] = useState(false);
  const [memberDropdownOpen, setMemberDropdownOpen] = useState(false);
  const [teamMembers, setTeamMembers] = useState<TeamMemberListItem[]>([]);
  const [teamMembersLoading, setTeamMembersLoading] = useState(false);
  const [teamMembersLoadFailed, setTeamMembersLoadFailed] = useState(false);
  const [selectedCreatedByUid, setSelectedCreatedByUid] = useState<string | undefined>(undefined);
  const [masonryColumnCount, setMasonryColumnCount] = useState(5);
  const [masonryGridWidth, setMasonryGridWidth] = useState(0);
  const [batchMode, setBatchMode] = useState(defaultBatchMode);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [deleteConfirmState, setDeleteConfirmState] = useState<HistoryDeleteConfirmState>(null);
  const [itemActionMenu, setItemActionMenu] = useState<{
    item: AssetItemView;
    left: number;
    top: number;
  } | null>(null);
  // 历史记录弹框批量「添加到资产库」：选目标文件夹的弹窗与入库进行态。
  const [addToLibraryDialogOpen, setAddToLibraryDialogOpen] = useState(false);
  const [addToLibraryFolders, setAddToLibraryFolders] = useState<FolderItem[]>([]);
  const [addToLibraryTargetFolderId, setAddToLibraryTargetFolderId] = useState<number | undefined>(undefined);
  const [savingSelectedToLibrary, setSavingSelectedToLibrary] = useState(false);
  const folderDropdownRef = useRef<HTMLDivElement>(null);
  const taskDropdownRef = useRef<HTMLDivElement>(null);
  const materialDropdownRef = useRef<HTMLDivElement>(null);
  const memberDropdownRef = useRef<HTMLDivElement>(null);
  const viewModeDropdownRef = useRef<HTMLDivElement>(null);
  const itemActionMenuRef = useRef<HTMLDivElement>(null);
  const [creatingEmptyCanvas, setCreatingEmptyCanvas] = useState(false);
  /**
   * 弹窗空状态「新建画布」：Team Studio 等已有 project 上下文时只创建画布并保留当前房间；
   * 其它宿主仍走 createNewAgentTeams。
   */
  const handleCreateEmptyCanvas = useCallback(async () => {
    if (creatingEmptyCanvas) return;
    const projectId = canvasProjectId?.trim();
    if (projectId) {
      setCreatingEmptyCanvas(true);
      try {
        const targetRoomId = resolveRoomIdByInsertTarget(insertTarget);
        if (!targetRoomId) throw new Error('missing current room_id');
        const { createCanvasInProject } = await import('$/domains/workFlowV3/teamCanvasService');
        const created = await createCanvasInProject({ project_id: projectId, namePrefix: canvasFallback });
        const targetCanvasId = created.canvas_id;
        setOpenAssetPopover?.(false);
        navigateMap.historyTeamFlow({
          room_id: targetRoomId,
          project_id: projectId,
          canvas_id: targetCanvasId,
        });
      } catch (error) {
        console.error('create canvas in project error:', error);
        message.error(languageStore.smartcatTranslationText((v) => v.create_canvas_failed));
      } finally {
        setCreatingEmptyCanvas(false);
      }
      return;
    }
    const { createNewAgentTeams: run } = await import('@/utils/createRoom');
    await run(trackLocation ?? 'asset');
  }, [
    canvasFallback,
    insertTarget,
    canvasProjectId,
    creatingEmptyCanvas,
    languageStore,
    navigateMap,
    setOpenAssetPopover,
    trackLocation,
  ]);
  /** 通用资产库选择成功埋点；发布弹窗等宿主自带专用参数时跳过这里的兜底上报。 */
  const trackAssetUploadSuccess = useCallback(
    (items: AssetItemView[]) => {
      if (!enableUploadSuccessTrack) return;
      trackRoboneoAssetUploadSuccess(items, insertTarget, trackLocation);
    },
    [enableUploadSuccessTrack, insertTarget, trackLocation],
  );
  const scrollContentRef = useRef<HTMLDivElement>(null);
  /** 与资产页一致：筛选变化时递增，丢弃过期的「加载更多」结果 */
  const assetListFetchEpochRef = useRef(0);
  const lastLoadedAssetPageRef = useRef(0);
  const accumulatedAssetRowsRef = useRef<AssetItem[]>([]);
  const loadMoreInFlightRef = useRef(false);
  const loadMoreAssetListRef = useRef<() => Promise<void>>(async () => {});
  const previousHistoryBatchModeForTrackRef = useRef(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [listHasMore, setListHasMore] = useState(false);
  useEffect(() => {
    const el = scrollContentRef.current;
    if (!el) return undefined;
    /** 使用布局宽度计算列数，避免弹框缩放动画影响 getBoundingClientRect 的测量结果。 */
    const update = () => {
      const w = el.clientWidth;
      setMasonryGridWidth(w);
      const next = masonryColumnCountForFixedCardWidth(w, ASSET_MODAL_MASONRY_CARD_WIDTH);
      setMasonryColumnCount((prev) => (prev === next ? prev : next));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);
  useEffect(() => {
    if (allowedMaterialTypeValues.length === 0) {
      return;
    }
    if (!allowedMaterialTypeValues.includes(materialType)) {
      setMaterialType(allowedMaterialTypeValues[0]);
    }
  }, [allowedMaterialTypeValues, materialType]);
  useEffect(() => {
    setSelectedCreatedByUid(undefined);
    setSelectedFolderId(undefined);
    setMemberDropdownOpen(false);
    setTeamMembers([]);
    setTeamMembersLoadFailed(false);

    if (!isTeamHistory) {
      setTeamMembersLoading(false);
      return undefined;
    }

    let isActive = true;
    setTeamMembersLoading(true);

    /**
     * 拉取当前团队成员，并忽略切换团队或组件卸载后的过期响应。
     */
    async function loadCurrentTeamMembers(): Promise<void> {
      try {
        const data = await getTeamMembers();
        if (isActive) {
          setTeamMembers(Array.isArray(data.list) ? data.list : []);
        }
      } catch {
        if (isActive) {
          setTeamMembers([]);
          setTeamMembersLoadFailed(true);
        }
      } finally {
        if (isActive) {
          setTeamMembersLoading(false);
        }
      }
    }

    loadCurrentTeamMembers().catch(() => {});

    return () => {
      isActive = false;
    };
  }, [isTeamHistory, teamOid]);
  /**
   * 与 apps/roboneo-web AssetManage `assetListBaseParams` 一致：不含 page，由首屏/滚动加载分别传入。
   */
  const assetListBaseParams = useMemo((): Omit<AssetListParams, 'page' | 'page_size'> => {
    const params: Omit<AssetListParams, 'page' | 'page_size'> = {
      task_type: taskType,
      material_type: materialType,
      sort_by: 'created_at',
      sort_order: sortOrder,
    };
    if (selectedFolderId != null) params.folder_id = selectedFolderId;
    if (selectedCreatedByUid != null) params.created_by_uid = selectedCreatedByUid;
    if (favoriteChecked) params.only_favorited = 1;
    return params;
  }, [taskType, materialType, selectedFolderId, selectedCreatedByUid, favoriteChecked, sortOrder]);
  /**
   * 拉取第一页并重建分组（筛选/排序/视图模式变化时由 effect 调用）。
   *
   * @param signal 可选；卸载时 abort，避免卸载后 setState
   */
  const loadAssetList = useCallback(
    async (signal?: AbortSignal): Promise<void> => {
      assetListFetchEpochRef.current += 1;
      const fetchEpoch = assetListFetchEpochRef.current;
      accumulatedAssetRowsRef.current = [];
      lastLoadedAssetPageRef.current = 0;
      setListHasMore(false);
      setLoading(true);
      try {
        const params: AssetListParams = {
          ...assetListBaseParams,
          page: 1,
          page_size: DEFAULT_PAGE_SIZE,
        };
        const res = await getAssetList(params);
        if (signal?.aborted) return;
        if (fetchEpoch !== assetListFetchEpochRef.current) return;
        accumulatedAssetRowsRef.current = res.list;
        lastLoadedAssetPageRef.current = res.list.length > 0 ? 1 : 0;
        const items = res.list.map(mapAssetRowToModalViewItem);
        setGroups(buildAssetModalGroupsFromList(listGroupMode, res.list, items, sortOrder));
        const total = Number.isFinite(res.total) ? res.total : items.length;
        setListHasMore(items.length > 0 && items.length < total);
      } catch (err) {
        if (signal?.aborted) return;
        if (fetchEpoch !== assetListFetchEpochRef.current) return;
        if (!isAssetLibraryApiMessageShown(err)) {
          message.error(languageStore.smartcatTranslationText((v) => v.get_assets_list_failed));
        }
      } finally {
        if (!signal?.aborted && fetchEpoch === assetListFetchEpochRef.current) {
          setLoading(false);
        }
      }
    },
    [sortOrder, listGroupMode, assetListBaseParams, languageStore, teamOid],
  );
  /**
   * 滚动接近底部时请求下一页，拼接到累加列表并重新分组（与 AssetManage `loadMoreAssetList` 一致）。
   */
  const loadMoreAssetList = useCallback(async (): Promise<void> => {
    if (!listHasMore || loading || loadingMore || loadMoreInFlightRef.current) {
      return;
    }
    const epochAtStart = assetListFetchEpochRef.current;
    const nextPage = lastLoadedAssetPageRef.current + 1;
    if (nextPage < 2) return;
    loadMoreInFlightRef.current = true;
    setLoadingMore(true);
    try {
      const params: AssetListParams = {
        ...assetListBaseParams,
        page: nextPage,
        page_size: DEFAULT_PAGE_SIZE,
      };
      const res = await getAssetList(params);
      if (epochAtStart !== assetListFetchEpochRef.current) return;
      if (res.list.length === 0) {
        setListHasMore(false);
        return;
      }
      const combinedList = [...accumulatedAssetRowsRef.current, ...res.list];
      accumulatedAssetRowsRef.current = combinedList;
      lastLoadedAssetPageRef.current = nextPage;
      const combinedItems = combinedList.map(mapAssetRowToModalViewItem);
      setGroups(buildAssetModalGroupsFromList(listGroupMode, combinedList, combinedItems, sortOrder));
      const total = Number.isFinite(res.total) ? res.total : combinedList.length;
      setListHasMore(combinedList.length < total);
    } catch (err) {
      if (epochAtStart !== assetListFetchEpochRef.current) return;
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.load_more_fail));
      }
    } finally {
      loadMoreInFlightRef.current = false;
      if (epochAtStart === assetListFetchEpochRef.current) {
        setLoadingMore(false);
      }
    }
  }, [listHasMore, loading, loadingMore, sortOrder, assetListBaseParams, listGroupMode, languageStore, teamOid]);
  useLayoutEffect(() => {
    loadMoreAssetListRef.current = loadMoreAssetList;
  }, [loadMoreAssetList]);
  useEffect(() => {
    const ac = new AbortController();
    loadAssetList(ac.signal).catch(() => {});
    return () => ac.abort();
  }, [loadAssetList]);
  /**
   * 列表区域滚动：距底部 400px 内触发加载下一页（与 AssetManage `handleScroll` 加载逻辑一致）。
   */
  const handleAssetListScroll = useCallback((e: React.UIEvent<HTMLDivElement>) => {
    const el = e.currentTarget;
    const loadMoreThresholdPx = 400;
    if (el.scrollHeight - el.scrollTop - el.clientHeight < loadMoreThresholdPx) {
      loadMoreAssetListRef.current().catch(() => {});
    }
  }, []);
  useEffect(() => {
    let isActive = true;
    getAssetFolderList()
      .then((res) => {
        if (!isActive) return;
        setFolders(
          res.folders.map((f) => ({
            id: String(f.id),
            name: f.name || unnamedFolderText,
            isPinned: f.is_pinned === 1,
            pinnedAt: Number(f.pinned_at) || 0,
          })),
        );
      })
      .catch((err) => {
        if (isActive && !isAssetLibraryApiMessageShown(err)) {
          message.error(languageStore.smartcatTranslationText((v) => v.assets_get_list_fail));
        }
      });
    return () => {
      isActive = false;
    };
  }, [languageStore, teamOid, unnamedFolderText]);
  const foldersOrdered = useMemo(() => sortFoldersForDropdown(folders), [folders]);
  const addToLibraryFoldersOrdered = useMemo(() => sortFoldersForDropdown(addToLibraryFolders), [addToLibraryFolders]);
  /**
   * 资产库弹层内：切换单个文件夹置顶状态（与资产页接口一致，可多文件夹同时置顶）。
   *
   * @param folder 目标文件夹
   */
  const handleToggleFolderPin = useCallback(
    async (folder: FolderItem) => {
      const folderId = Number(folder.id);
      if (!Number.isFinite(folderId)) {
        message.error(languageStore.smartcatTranslationText((v) => v.assets_invalid_folder_id));
        return;
      }
      const nextPinned = !folder.isPinned;
      try {
        await pinAssetFolder(folderId, nextPinned ? 1 : 0);
        const res = await getAssetFolderList();
        setFolders(
          res.folders.map((f) => ({
            id: String(f.id),
            name: f.name || unnamedFolderText,
            isPinned: f.is_pinned === 1,
            pinnedAt: Number(f.pinned_at) || 0,
          })),
        );
        message.success(
          nextPinned
            ? languageStore.smartcatTranslationText((v) => v.set_to_top_asset)
            : languageStore.smartcatTranslationText((v) => v.asset_unpin_from_top),
        );
      } catch (err) {
        if (!isAssetLibraryApiMessageShown(err)) {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        }
      }
    },
    [languageStore, unnamedFolderText],
  );
  /** 批量保存资产库上传项；资产库视图同步刷新目录树，历史记录视图只刷新自身列表。 */
  const persistMediaLibraryUploadItems = useCallback(
    async (
      paramsList: Array<MediaLibraryItemSaveParams | null>,
      options?: {
        showSuccessMessage?: boolean;
      },
    ): Promise<boolean> => {
      const showSuccessMessage = options?.showSuccessMessage !== false;
      const validParams = paramsList.filter((params): params is MediaLibraryItemSaveParams => params != null);
      if (!validParams.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.images_videos_and_audio_files_can_be_uploaded_));
        return false;
      }
      const results = await Promise.allSettled(validParams.map((params) => saveMediaLibraryItem(params)));
      const successCount = results.filter((result) => result.status === 'fulfilled').length;
      const failedResults = results.filter((result): result is PromiseRejectedResult => result.status === 'rejected');
      if (successCount > 0) {
        await loadAssetList();
        if (showSuccessMessage) {
          message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_web_confirm));
        }
      }
      if (failedResults.length > 0 && successCount > 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      if (
        failedResults.length > 0
        && successCount === 0
        && !failedResults.some((result) => isAssetLibraryApiMessageShown(result.reason))
      ) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      return successCount > 0;
    },
    [languageStore, loadAssetList],
  );
  /** 历史记录上传复用本地上传数量上限，超出的选择项直接跳过并提示。 */
  const limitMediaLibraryHistoryUploadItems = useCallback(
    (items: AssetItemView[]): AssetItemView[] => {
      const counts: Record<MediaLibraryUploadMaterialType, number> = {
        image: 0,
        video: 0,
        audio: 0,
      };
      let exceeded = false;
      const accepted: AssetItemView[] = [];
      items.forEach((item) => {
        const uploadMaterialType = resolveAssetViewMediaLibraryMaterialType(item);
        if (!uploadMaterialType) return;
        if (counts[uploadMaterialType] >= MEDIA_LIBRARY_HISTORY_UPLOAD_LIMITS[uploadMaterialType]) {
          exceeded = true;
          return;
        }
        counts[uploadMaterialType] += 1;
        accepted.push(item);
      });
      if (exceeded) {
        message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_file_limit));
      }
      return accepted;
    },
    [languageStore],
  );
  const visibleItemsOrdered = useMemo(() => groups.flatMap((g) => g.items), [groups]);
  /** 资产库弹窗曝光：组件挂载一次上报一次，并保留弹窗级兼容事件。 */
  useEffect(() => {
    trackRoboneoAssetLibraryShow(insertTarget);
    // 仅在弹窗挂载时上报一次，room_id 由 insertTarget 解析。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  /** 已上报曝光的卡片 id，避免列表刷新 / 滚动加载时重复上报。 */
  const reportedCardShowIdsRef = useRef<Set<string>>(new Set());
  /** 素材库卡片曝光：可见列表新增卡片时按 id 去重上报。 */
  useEffect(() => {
    visibleItemsOrdered.forEach((item) => {
      if (reportedCardShowIdsRef.current.has(item.id)) return;
      reportedCardShowIdsRef.current.add(item.id);
      trackRoboneoAssetLibraryCardShow(item, insertTarget);
    });
  }, [visibleItemsOrdered, insertTarget]);
  /** 历史记录批量操作只读取当前可见且已选中的条目。 */
  const selectedHistoryAssetItems = useMemo(() => {
    return visibleItemsOrdered.filter((item) => selectedIds.has(item.id));
  }, [selectedIds, visibleItemsOrdered]);
  /** 历史记录批量接口统一使用 asset_id，不复用资产库 item_id。 */
  const selectedHistoryAssetIds = useMemo(() => {
    return selectedHistoryAssetItems.map(getHistoryAssetNumericId).filter((id): id is number => id != null);
  }, [selectedHistoryAssetItems]);
  const selectedHistoryAssetsAllFavorited = selectedHistoryAssetItems.length > 0 && selectedHistoryAssetItems.every((item) => item.isFavorite);
  /** 批量「全选」作用域：内置插入时仅为当前目标支持的类型，否则为当前列表全部可见项 */
  const batchSelectScopeItems = useMemo(() => {
    if (!limitBuiltInBatchMedia) return visibleItemsOrdered;
    return visibleItemsOrdered.filter((i) => isBuiltInModalBatchSelectableMaterialType(insertTarget, i.materialType));
  }, [limitBuiltInBatchMedia, insertTarget, visibleItemsOrdered]);
  const batchSelectScopeCount = batchSelectScopeItems.length;
  const visibleIdSet = useMemo(() => new Set(visibleItemsOrdered.map((i) => i.id)), [visibleItemsOrdered]);
  useEffect(() => {
    setSelectedIds((prev) => {
      const next = new Set<string>();
      let changed = false;
      prev.forEach((id) => {
        if (visibleIdSet.has(id)) next.add(id);
        else changed = true;
      });
      if (!changed && next.size === prev.size) return prev;
      return next;
    });
  }, [visibleIdSet]);
  const exitBatchMode = useCallback(() => {
    setSelectedIds(new Set());
    setBatchMode(false);
  }, []);
  /**
   * 多选底栏「取消选择」：清空已选，仍停留在批量模式（对齐 Figma 33766:106933）。
   */
  const clearBatchSelectionOnly = useCallback(() => {
    setSelectedIds(new Set());
  }, []);
  const toggleBatchMode = useCallback(() => {
    setBatchMode((v) => {
      if (v) setSelectedIds(new Set());
      return !v;
    });
    setFolderDropdownOpen(false);
    setTaskDropdownOpen(false);
    setMaterialDropdownOpen(false);
    setMemberDropdownOpen(false);
    setViewModeDropdownOpen(false);
  }, []);
  /** 在主体选择上限内切换单条历史记录选中态。 */
  const toggleSelectAssetId = useCallback((id: string) => {
    setSelectedIds((prev) => toggleBatchAssetSelection(
      prev,
      id,
      subjectPickerMaxSelectableCount,
    ));
  }, [subjectPickerMaxSelectableCount]);
  /**
   * 批量模式下切换单条选中，并通知外部（卡片点击、勾选框与键盘操作共用）。
   *
   * @param item 列表项
   */
  const handleBatchItemToggle = useCallback(
    (item: AssetItemView) => {
      if (limitBuiltInBatchMedia && !isBuiltInModalBatchSelectableMaterialType(insertTarget, item.materialType)) {
        return;
      }
      if (!canToggleBatchAssetSelection(selectedIds, item.id, subjectPickerMaxSelectableCount)) {
        return;
      }
      const wasSelected = selectedIds.has(item.id);
      const selected = !wasSelected;
      toggleSelectAssetId(item.id);
      onAssetBatchItemClick?.({ item, selected });
    },
    [
      insertTarget,
      limitBuiltInBatchMedia,
      onAssetBatchItemClick,
      selectedIds,
      subjectPickerMaxSelectableCount,
      toggleSelectAssetId,
    ],
  );
  /** 全选时同样截取到外部选择上限，避免隐藏入口绕过约束。 */
  const toggleSelectAllVisible = useCallback(() => {
    setSelectedIds((prev) => {
      if (batchSelectScopeCount === 0) return prev;
      const allOn = batchSelectScopeItems.every((i) => prev.has(i.id));
      if (allOn) return new Set();
      const nextIds = batchSelectScopeItems.map((i) => i.id);
      return new Set(subjectPickerMaxSelectableCount == null
        ? nextIds
        : nextIds.slice(0, subjectPickerMaxSelectableCount));
    });
  }, [batchSelectScopeCount, batchSelectScopeItems, subjectPickerMaxSelectableCount]);
  /** 打开历史记录单条删除确认，确认后必须调用历史记录删除接口。 */
  const openDeleteHistoryAssetConfirm = useCallback((item: AssetItemView) => {
    setDeleteConfirmState({ kind: 'historyItem', item });
    setItemActionMenu(null);
  }, []);
  /** 收藏或取消收藏历史记录条目，成功后重拉列表以同步收藏筛选、分页和分组。 */
  const handleToggleHistoryAssetFavorite = useCallback(
    async (item: AssetItemView) => {
      const assetId = getHistoryAssetNumericId(item);
      if (!assetId) {
        message.error(languageStore.smartcatTranslationText((v) => v.asset_id_is_invalid));
        return;
      }
      const nextFavorited = item.isFavorite ? 0 : 1;
      try {
        await starAsset(assetId, nextFavorited);
        trackRoboneoHistoryCollectAction([item], 'single', nextFavorited === 1 ? 'collect' : 'cancel');
        await loadAssetList();
        message.success(
          nextFavorited === 1
            ? languageStore.smartcatTranslationText((v) => v.collected_asset)
            : languageStore.smartcatTranslationText((v) => v.assets_unfavourited),
        );
      } catch (err) {
        if (!isAssetLibraryApiMessageShown(err)) {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        }
      }
    },
    [languageStore, loadAssetList],
  );
  /** 打开历史记录批量删除确认，确认后统一调用历史记录批量删除接口。 */
  const openDeleteHistoryAssetBatchConfirm = useCallback(() => {
    if (selectedHistoryAssetItems.length === 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    if (selectedHistoryAssetIds.length !== selectedHistoryAssetItems.length) {
      message.error(languageStore.smartcatTranslationText((v) => v.selected_asset_id_is_invalid));
      return;
    }
    setDeleteConfirmState({ kind: 'historyBatch', assetIds: selectedHistoryAssetIds });
  }, [languageStore, selectedHistoryAssetIds, selectedHistoryAssetItems.length]);
  /** 批量收藏或取消收藏历史记录条目，并刷新列表后退出批量模式。 */
  const handleBatchHistoryAssetFavorite = useCallback(async () => {
    if (selectedHistoryAssetItems.length === 0) {
      message.info(languageStore.smartcatTranslationText((v) => v.select_asset_to_favourite));
      return;
    }
    if (selectedHistoryAssetIds.length !== selectedHistoryAssetItems.length) {
      message.error(languageStore.smartcatTranslationText((v) => v.selected_asset_id_is_invalid));
      return;
    }
    try {
      await batchActionAsset(selectedHistoryAssetIds, selectedHistoryAssetsAllFavorited ? 'unstar' : 'star');
      trackRoboneoHistoryCollectAction(
        selectedHistoryAssetItems,
        'bath',
        selectedHistoryAssetsAllFavorited ? 'cancel' : 'collect',
      );
      await loadAssetList();
      exitBatchMode();
      message.success(
        selectedHistoryAssetsAllFavorited
          ? languageStore.smartcatTranslationText((v) => v.assets_unfavourited)
          : languageStore.smartcatTranslationText((v) => v.collected_asset),
      );
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.batch_favourite_fail));
      }
    }
  }, [
    exitBatchMode,
    languageStore,
    loadAssetList,
    selectedHistoryAssetIds,
    selectedHistoryAssetItems,
    selectedHistoryAssetsAllFavorited,
  ]);
  /** 按历史记录语义执行单条或批量删除。 */
  const handleConfirmAssetDelete = useCallback(async () => {
    if (!deleteConfirmState) return;
    try {
      if (deleteConfirmState.kind === 'historyItem') {
        const assetId = getHistoryAssetNumericId(deleteConfirmState.item);
        if (!assetId) throw new Error('invalid history asset id');
        await deleteAsset(assetId);
        trackRoboneoHistoryContentDelete([deleteConfirmState.item], 'single');
        setSelectedIds((prev) => {
          const next = new Set(prev);
          next.delete(deleteConfirmState.item.id);
          return next;
        });
        await loadAssetList();
        message.success(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_success));
      } else if (deleteConfirmState.kind === 'historyBatch') {
        await deleteAsset(deleteConfirmState.assetIds);
        trackRoboneoHistoryContentDelete(selectedHistoryAssetItems, 'bath');
        exitBatchMode();
        await loadAssetList();
        message.success(
          deleteConfirmState.assetIds.length === 1
            ? languageStore.smartcatTranslationText((v) => v.deleted_assets)
            : languageStore
              .smartcatTranslationText((v) => v.deleted_num_assets)
              .replace(/\$\{数量\}/g, String(deleteConfirmState.assetIds.length)),
        );
      }
      setDeleteConfirmState(null);
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_delete_fail));
      }
    }
  }, [deleteConfirmState, exitBatchMode, languageStore, loadAssetList, selectedHistoryAssetItems]);
  useEffect(() => {
    /** 点击所有弹层外部时统一关闭筛选、更多菜单和上传菜单。 */
    const handleClick = (e: MouseEvent) => {
      const target = e.target as Node;
      if (folderDropdownRef.current?.contains(target)) return;
      if (taskDropdownRef.current?.contains(target)) return;
      if (materialDropdownRef.current?.contains(target)) return;
      if (memberDropdownRef.current?.contains(target)) return;
      if (viewModeDropdownRef.current?.contains(target)) return;
      if (itemActionMenuRef.current?.contains(target)) return;
      setFolderDropdownOpen(false);
      setTaskDropdownOpen(false);
      setMaterialDropdownOpen(false);
      setMemberDropdownOpen(false);
      setViewModeDropdownOpen(false);
      setItemActionMenu(null);
    };
    document.addEventListener('mousedown', handleClick);
    return () => document.removeEventListener('mousedown', handleClick);
  }, []);
  const toggleSortOrder = useCallback(() => {
    setSortOrder((o) => (o === 'asc' ? 'desc' : 'asc'));
  }, []);
  const selectedFolderName = useMemo(() => {
    const allLabel = languageStore.smartcatTranslationText((v) => v.roboneo_web_All);
    if (selectedFolderId == null) return allLabel;
    return folders.find((f) => f.id === String(selectedFolderId))?.name ?? allLabel;
  }, [selectedFolderId, folders, languageStore]);
  /** 历史记录从非批量切到批量模式时上报一次编辑栏曝光。 */
  useEffect(() => {
    if (batchMode && !previousHistoryBatchModeForTrackRef.current) {
      trackRoboneoHistoryBatchBarShow(selectedFolderName);
    }
    previousHistoryBatchModeForTrackRef.current = batchMode;
  }, [batchMode, selectedFolderName]);
  const taskTypeLabel = useMemo(() => getTaskTypeFilterLabel(taskType), [getTaskTypeFilterLabel, taskType]);
  const reportedTeamHistoryCardShowKeysRef = useRef<Set<string>>(new Set());
  /** 团队历史卡片首次进入视口时，按筛选 tab 和卡片 ID 去重上报曝光。 */
  const trackCurrentTeamHistoryCardShow = useCallback((item: AssetItemView) => {
    const reportKey = `${taskType}:${item.id}`;
    if (reportedTeamHistoryCardShowKeysRef.current.has(reportKey)) return;
    reportedTeamHistoryCardShowKeysRef.current.add(reportKey);
    trackRoboneoHistoryMedia('show', item);
    if (!isTeamHistory) return;
    trackTeamHistoryCardShow({
      item,
      positionId: Math.max(0, visibleItemsOrdered.findIndex((visibleItem) => visibleItem.id === item.id)),
      tabId: taskType,
      tabName: taskTypeLabel,
      projectId: canvasProjectId,
    });
  }, [canvasProjectId, isTeamHistory, taskType, taskTypeLabel, visibleItemsOrdered]);
  /** 团队历史卡片激活时按当前列表顺序上报点击。 */
  const trackCurrentTeamHistoryCardClick = useCallback((item: AssetItemView) => {
    trackRoboneoHistoryMedia('click', item);
    if (!isTeamHistory) return;
    trackTeamHistoryCardClick({
      item,
      positionId: Math.max(0, visibleItemsOrdered.findIndex((visibleItem) => visibleItem.id === item.id)),
      tabId: taskType,
      tabName: taskTypeLabel,
      projectId: canvasProjectId,
    });
  }, [canvasProjectId, isTeamHistory, taskType, taskTypeLabel, visibleItemsOrdered]);
  const materialTypeLabel = useMemo(
    () => materialOptionsVisible.find((o) => o.value === materialType)?.label
      ?? languageStore.smartcatTranslationText((v) => v.all),
    [materialType, materialOptionsVisible, languageStore],
  );
  /** 为成员筛选项补充“我”标记，并保持按钮和菜单使用同一套成员名称。 */
  const getTeamMemberLabel = useCallback((member: TeamMemberListItem): string => {
    const displayName = getTeamMemberDisplayName(member);
    if (currentUserUid && String(member.uid) === String(currentUserUid)) {
      return `${displayName}${currentUserSuffixText}`;
    }
    return displayName;
  }, [currentUserSuffixText, currentUserUid]);
  const selectedTeamMember = useMemo(() => {
    return teamMembers.find((member) => String(member.uid) === selectedCreatedByUid);
  }, [selectedCreatedByUid, teamMembers]);
  const teamMemberFilterLabel = selectedTeamMember
    ? getTeamMemberLabel(selectedTeamMember)
    : filterByMemberText;
  /** 点击已选成员会清除筛选；点击其他成员则按其创建人 UID 重新拉取历史记录。 */
  const handleTeamMemberSelect = useCallback((member: TeamMemberListItem) => {
    const memberUid = String(member.uid);
    setSelectedCreatedByUid((selectedUid) => (selectedUid === memberUid ? undefined : memberUid));
    setMemberDropdownOpen(false);
  }, []);
  /** 將項目分配到瀑布流列：列數取 masonryColumnCount 與實際項目數的較小值，避免產生空列 */
  const masonryColumns = useMemo(() => {
    return (items: AssetItemView[]) => {
      const n = Math.min(masonryColumnCount, items.length) || 1;
      const colW = masonryGridWidth > 0 ? ASSET_MODAL_MASONRY_CARD_WIDTH : 0;
      const cols: AssetItemView[][] = Array.from({ length: n }, () => []);
      const columnHeights: number[] = new Array(n).fill(0);
      items.forEach((item) => {
        const minHeightIndex = columnHeights.indexOf(Math.min(...columnHeights));
        cols[minHeightIndex].push(item);
        const isAudio = item.materialType === 'audio';
        const share = isAudio && colW > 0 ? masonryAudioItemHeightShare(colW) : 1 / item.aspectRatio;
        columnHeights[minHeightIndex] += share;
      });
      return cols;
    };
  }, [masonryColumnCount, masonryGridWidth]);
  /** 打开某个下拉前关闭其它同级下拉，避免工具栏菜单互相叠压。 */
  const closeOtherDropdowns = useCallback((except: 'folder' | 'task' | 'material' | 'member' | 'viewMode') => {
    if (except !== 'folder') setFolderDropdownOpen(false);
    if (except !== 'task') setTaskDropdownOpen(false);
    if (except !== 'material') setMaterialDropdownOpen(false);
    if (except !== 'member') setMemberDropdownOpen(false);
    if (except !== 'viewMode') setViewModeDropdownOpen(false);
  }, []);
  /** 打开素材卡片更多菜单，并关闭其它悬浮菜单。 */
  const openItemActionMenu = useCallback((item: AssetItemView, anchorEl: HTMLElement) => {
    const rect = anchorEl.getBoundingClientRect();
    setItemActionMenu({
      item,
      left: Math.max(8, rect.right - 124),
      top: rect.bottom + 6,
    });
  }, []);
  useLayoutEffect(() => {
    if (!itemActionMenu) return;
    const rect = itemActionMenuRef.current?.getBoundingClientRect();
    if (!rect) return;
    const nextLeft = Math.max(8, Math.min(itemActionMenu.left, window.innerWidth - rect.width - 8));
    const nextTop = Math.max(8, Math.min(itemActionMenu.top, window.innerHeight - rect.height - 8));
    if (nextLeft !== itemActionMenu.left || nextTop !== itemActionMenu.top) {
      setItemActionMenu({ ...itemActionMenu, left: nextLeft, top: nextTop });
    }
  }, [itemActionMenu]);
  const insertHint = useMemo(
    () => assetActionLabel
      ?? languageStore.smartcatTranslationText((v) => (insertTarget === 'workflow' ? v.asset_send_to_workflow : v.add_to_canvas)),
    [assetActionLabel, languageStore, insertTarget],
  );
  const batchPrimaryLabel = useMemo(
    () => batchConfirmLabel
      ?? languageStore.smartcatTranslationText((v) => (insertTarget === 'workflow' ? v.asset_send_to_workflow : v.add_to_canvas)),
    [batchConfirmLabel, languageStore, insertTarget],
  );
  /** 将单条资产添加到外部托管场景、工作流或画布，并在成功后按场景上报埋点。 */
  const handleAddToCanvas = useCallback(
    async (item: AssetItemView) => {
      const { mediaUrl, materialType: itemMaterialType } = item;
      const hasScreenplayFile = itemMaterialType === 'script'
        && item.raw.episodes?.some((episode) => episode.urls?.length > 0);
      if (!mediaUrl?.trim() && !hasScreenplayFile) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      if (enableAddCanvasClickTrack) {
        trackRoboneoAssetAddCanvasClick(item, insertTarget);
      }
      if (onAssetItemClick) {
        onAssetItemClick(item);
        trackAssetUploadSuccess([item]);
        return;
      }
      if (insertTarget === 'workflow') {
        if (!onInsertWorkflowMedia) {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
          return;
        }
        if (!isWorkflowInsertableMaterialType(itemMaterialType)) {
          message.warning(languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_on_in_workflow));
          return;
        }
        const wItem = assetItemToWorkflowMediaItem(item);
        if (!wItem) {
          message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
          return;
        }
        try {
          onInsertWorkflowMedia([wItem]);
          trackAssetUploadSuccess([item]);
          if (closeOnBatchConfirm) {
            setOpenAssetPopover?.(false);
          }
        } catch {
          message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        }
        return;
      }
      if (!isCanvasInsertableMaterialType(itemMaterialType)) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_in_canvas));
        return;
      }
      try {
        await insertAssetItemToCanvas(item);
        trackAssetUploadSuccess([item]);
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
      } catch {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
    },
    [
      enableAddCanvasClickTrack,
      insertTarget,
      languageStore,
      onInsertWorkflowMedia,
      onAssetItemClick,
      closeOnBatchConfirm,
      setOpenAssetPopover,
      trackAssetUploadSuccess,
    ],
  );
  /**
   * 预览弹框内是否展示添加按钮，与卡片 hover 添加能力保持一致。
   */
  const shouldShowPreviewAddButton = useCallback(
    (item: AssetItemView): boolean => {
      return Boolean(onAssetItemClick) || shouldShowAssetInsertHoverBadge(insertTarget, item.materialType);
    },
    [insertTarget, onAssetItemClick],
  );
  /**
   * 构造公共预览头部/底部的添加按钮插槽。
   */
  const buildAssetPreviewAddSlot = useCallback(
    (item: AssetItemView, previewItems?: AssetItemView[]): React.ReactElement | null => {
      if (!shouldShowPreviewAddButton(item)) return null;
      return (
        <AssetPreviewAddToCanvasSlot
          item={item}
          previewItems={previewItems}
          label={insertHint}
          onAddToCanvas={handleAddToCanvas}
        />
      );
    },
    [handleAddToCanvas, insertHint, shouldShowPreviewAddButton],
  );
  /** 批量添加资产到外部托管场景、工作流或画布，并按实际成功列表上报埋点。 */
  const handleBatchAdd = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    const ordered = visibleItemsOrdered.filter((i) => selectedIds.has(i.id));
    if (enableAddCanvasClickTrack) {
      ordered.forEach((item) => trackRoboneoAssetAddCanvasClick(item, insertTarget));
    }
    if (onAssetBatchConfirm) {
      onAssetBatchConfirm(ordered);
      trackAssetUploadSuccess(ordered);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      exitBatchMode();
      return;
    }
    if (insertTarget === 'workflow') {
      if (!onInsertWorkflowMedia) {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
        return;
      }
      const workflowAllowed = ordered.filter((i) => isWorkflowInsertableMaterialType(i.materialType));
      const skippedWorkflowUnsupported = ordered.length - workflowAllowed.length;
      if (skippedWorkflowUnsupported > 0) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_workflow));
      }
      const wItems = workflowAllowed
        .map(assetItemToWorkflowMediaItem)
        .filter((x): x is WorkflowCanvasMediaItem => x != null);
      if (!wItems.length) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      try {
        onInsertWorkflowMedia(wItems);
        trackAssetUploadSuccess(workflowAllowed);
        if (closeOnBatchConfirm) {
          setOpenAssetPopover?.(false);
        }
        exitBatchMode();
      } catch {
        message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
      }
      return;
    }
    const canvasItems = ordered.filter((i) => isCanvasInsertableMaterialType(i.materialType));
    const skippedCanvasUnsupported = ordered.length - canvasItems.length;
    if (skippedCanvasUnsupported > 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_canvas));
    }
    const withUrl = canvasItems.filter((i) => i.mediaUrl?.trim());
    if (!withUrl.length) {
      message.warning(languageStore.smartcatTranslationText((v) => v.assets_selected_cannot_be_opened_in_canvas));
      return;
    }
    let ok = 0;
    let fail = 0;
    const successItems: AssetItemView[] = [];
    /* 顺序插入避免画布节点在同一落点堆叠；需等待上一张完成再插下一张 */
    for (const item of withUrl) {
      try {
        // eslint-disable-next-line no-await-in-loop -- 批量插入画布必须串行
        await insertAssetItemToCanvas(item);
        ok += 1;
        successItems.push(item);
      } catch {
        fail += 1;
      }
    }
    if (ok > 0) {
      trackAssetUploadSuccess(successItems);
      if (closeOnBatchConfirm) {
        setOpenAssetPopover?.(false);
      }
      exitBatchMode();
    }
    if (fail > 0 && ok === 0) {
      message.error(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    } else if (fail > 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.roboneo_failed_try_again));
    }
  }, [
    selectedIds,
    visibleItemsOrdered,
    closeOnBatchConfirm,
    enableAddCanvasClickTrack,
    insertTarget,
    onInsertWorkflowMedia,
    onAssetBatchConfirm,
    setOpenAssetPopover,
    exitBatchMode,
    languageStore,
    trackAssetUploadSuccess,
  ]);
  /** 历史记录批量入库每次打开都拉取最新资产库目录，并与历史记录文件夹状态隔离。 */
  const openAddSelectedToLibraryDialog = useCallback(async () => {
    if (selectedIds.size === 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    try {
      const res = await getMediaLibraryFolderTree();
      setAddToLibraryFolders(flattenMediaLibraryFolderTree(
        res.list,
        unnamedFolderText,
        generatedCharacterText,
      ));
    } catch (err) {
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.assets_get_list_fail));
      }
      return;
    }
    setAddToLibraryTargetFolderId(undefined);
    setAddToLibraryDialogOpen(true);
  }, [generatedCharacterText, languageStore, selectedIds.size, unnamedFolderText]);
  /** 将历史记录弹框中批量勾选的素材保存到所选资产库文件夹。 */
  const handleSubmitAddSelectedToLibrary = useCallback(async () => {
    if (addToLibraryTargetFolderId == null) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_a_folder));
      return;
    }
    const ordered = visibleItemsOrdered.filter((item) => selectedIds.has(item.id));
    const limitedItems = limitMediaLibraryHistoryUploadItems(ordered);
    if (!limitedItems.length) {
      message.warning(languageStore.smartcatTranslationText((v) => v.images_videos_and_audio_files_can_be_uploaded_));
      return;
    }
    setSavingSelectedToLibrary(true);
    try {
      const paramsList = await Promise.all(
        limitedItems.map((item) => buildMediaLibrarySaveParamsFromHistoryItem(item, addToLibraryTargetFolderId)),
      );
      const saved = await persistMediaLibraryUploadItems(paramsList);
      if (saved) {
        setAddToLibraryDialogOpen(false);
        setAddToLibraryTargetFolderId(undefined);
        exitBatchMode();
      }
    } finally {
      setSavingSelectedToLibrary(false);
    }
  }, [
    addToLibraryTargetFolderId,
    exitBatchMode,
    languageStore,
    limitMediaLibraryHistoryUploadItems,
    persistMediaLibraryUploadItems,
    selectedIds,
    visibleItemsOrdered,
  ]);
  const assetPreviewSaveLocation: AssetPreviewSaveLocation = isTeamHistory
    ? 'team_history'
    : 'history_preview_page';
  /** 复用全局 MediaPreview 打开图片预览，发送到画布入口的卡片点击不再承担插入职责。 */
  const handleAssetImagePreview = useCallback(
    (item: AssetItemView) => {
      const { previewItems, sourceItems, currentIndex } = buildAssetMediaPreviewList(
        visibleItemsOrdered,
        item,
        assetPreviewSaveLocation,
      );
      if (previewItems.length === 0) return;
      openPreview({
        previewImageList: previewItems,
        initialIndex: currentIndex,
        previewShowZoom: false,
        previewShowDownload: canDownload,
        previewHeaderMenuSlots: buildAssetPreviewAddSlot(item, sourceItems),
      });
    },
    [assetPreviewSaveLocation, buildAssetPreviewAddSlot, canDownload, visibleItemsOrdered],
  );
  /** 复用全局 MediaPreview 打开文本/脚本预览。 */
  const handleAssetDocumentTextPreview = useCallback(
    (item: AssetItemView) => {
      const previewItem = buildAssetDocumentTextPreviewItem(item, assetPreviewSaveLocation);
      if (!previewItem) return;
      openPreview({
        previewImageList: [previewItem],
        initialIndex: 0,
        previewShowZoom: false,
        previewShowDownload: canDownload,
        previewHeaderMenuSlots: buildAssetPreviewAddSlot(item, [item]),
      });
    },
    [assetPreviewSaveLocation, buildAssetPreviewAddSlot, canDownload],
  );
  /** 根据素材类型打开对应预览。 */
  const handleAssetCardPreview = useCallback(
    (item: AssetItemView) => {
      if (isAssetImagePreviewable(item) || isAssetVideoPreviewable(item)) {
        handleAssetImagePreview(item);
        return;
      }
      handleAssetDocumentTextPreview(item);
    },
    [handleAssetDocumentTextPreview, handleAssetImagePreview],
  );
  /** 鼠标点击 hover 操作按钮后清理焦点，避免鼠标移出卡片后因 focus-within 仍显示操作层。 */
  const blurHoverActionButtonAfterMouseClick = useCallback((e: React.MouseEvent<HTMLButtonElement>) => {
    if (e.detail > 0) {
      e.currentTarget.blur();
    }
  }, []);
  /** 资产卡片菜单中的下载入口；历史记录按国内水印偏好选择地址。 */
  const handleAssetItemDownload = useCallback(
    async (item: AssetItemView) => {
      setItemActionMenu(null);
      const originalUrl = (item.mediaUrl || item.coverUrl || '').trim();
      if (!originalUrl) {
        message.warning(languageStore.smartcatTranslationText((v) => v.assets_no_downloadable_address));
        return;
      }
      if (isTeamHistory) {
        trackTeamAssetHistorySave('canvas_save_click', [item], 'team_history');
      }
      let downloadUrl = originalUrl;
      const watermarkUrl = getAssetWatermarkUrl(item);
      const isOversea = useOfficialStore.getState().isOversea();
      if (!isOversea && watermarkUrl) {
        const preference = await DownloadPreferencePopup.show();
        if (preference === '2') return;
        if (preference === '1') downloadUrl = watermarkUrl;
      }
      const hideLoading = message.loading('RoboNeo_Web_loading1', 0);
      try {
        await downloadFileFromUrl(downloadUrl, getAssetModalDownloadFilename(item), {
          preferSuggestedFilename: true,
        });
        if (isTeamHistory) {
          trackTeamAssetHistorySave('canvas_save_success', [item], 'team_history');
        }
      } catch {
        if (isTeamHistory) {
          trackTeamAssetHistorySave('canvas_save_fail', [item], 'team_history');
        }
        message.error(languageStore.smartcatTranslationText((v) => v.assets_download_fail));
      } finally {
        hideLoading();
      }
    },
    [isTeamHistory, languageStore],
  );
  /** 历史记录批量下载：单条直链，多条提交 ZIP 打包任务并轮询下载。 */
  const handleBatchHistoryAssetDownload = useCallback(async () => {
    if (selectedHistoryAssetItems.length === 0) {
      message.warning(languageStore.smartcatTranslationText((v) => v.please_select_asset_first));
      return;
    }
    if (selectedHistoryAssetIds.length !== selectedHistoryAssetItems.length) {
      message.error(languageStore.smartcatTranslationText((v) => v.did_not_find_asset));
      return;
    }
    if (selectedHistoryAssetItems.length === 1) {
      await handleAssetItemDownload(selectedHistoryAssetItems[0]!);
      return;
    }
    if (isTeamHistory) {
      trackTeamAssetHistorySave(
        'canvas_save_click',
        selectedHistoryAssetItems,
        'team_history',
      );
    }
    const isOversea = useOfficialStore.getState().isOversea();
    let withWatermark: number | undefined;
    if (!isOversea) {
      const preference = await DownloadPreferencePopup.show();
      if (preference === '2') return;
      withWatermark = preference === '1' ? 1 : 0;
    }
    const hideLoading = message.loading('RoboNeo_Web_loading1', 0);
    try {
      const submitResult = await downloadAssetFolder({
        asset_ids: selectedHistoryAssetIds,
        with_watermark: withWatermark,
      });
      const statusResult = await waitForHistoryAssetBatchDownloadTask(submitResult.task_id);
      if (!statusResult) {
        if (isTeamHistory) {
          trackTeamAssetHistorySave(
            'canvas_save_fail',
            selectedHistoryAssetItems,
            'team_history',
          );
        }
        message.error(languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_network_fail));
        return;
      }
      if (statusResult.status === 'success') {
        if (!statusResult.download_url) {
          if (isTeamHistory) {
            trackTeamAssetHistorySave(
              'canvas_save_fail',
              selectedHistoryAssetItems,
              'team_history',
            );
          }
          return;
        }
        await downloadFileFromUrl(statusResult.download_url, statusResult.file_name?.trim() || '素材打包.zip');
        if (isTeamHistory) {
          trackTeamAssetHistorySave(
            'canvas_save_success',
            selectedHistoryAssetItems,
            'team_history',
          );
        }
        return;
      }
      if (isTeamHistory) {
        trackTeamAssetHistorySave(
          'canvas_save_fail',
          selectedHistoryAssetItems,
          'team_history',
        );
      }
      message.error(
        statusResult.error_msg || languageStore.smartcatTranslationText((v) => v.roboneo_web_export_failed),
      );
    } catch (err) {
      if (isTeamHistory) {
        trackTeamAssetHistorySave(
          'canvas_save_fail',
          selectedHistoryAssetItems,
          'team_history',
        );
      }
      if (!isAssetLibraryApiMessageShown(err)) {
        message.error(languageStore.smartcatTranslationText((v) => v.assets_download_fail));
      }
    } finally {
      hideLoading();
    }
  }, [
    handleAssetItemDownload,
    isTeamHistory,
    languageStore,
    selectedHistoryAssetIds,
    selectedHistoryAssetItems,
  ]);
  const allVisibleSelected = batchSelectScopeCount > 0
    && selectedIds.size === batchSelectScopeCount
    && batchSelectScopeItems.every((i) => selectedIds.has(i.id));
  const someVisibleSelected = batchSelectScopeCount > 0 && batchSelectScopeItems.some((i) => selectedIds.has(i.id)) && !allVisibleSelected;
  const deleteConfirmContent = useMemo(() => {
    if (!deleteConfirmState) {
      return {
        title: '',
        note: '',
        confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
      };
    }
    if (deleteConfirmState.kind === 'historyBatch') {
      const selectedCount = deleteConfirmState.assetIds.length;
      return {
        title: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_delete_hint_text),
        note: languageStore
          .smartcatTranslationText((v) => v.asset_confirm_deletion_dialog_multiple_selects)
          .replace('{个数}', String(selectedCount)),
        confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
      };
    }
    return {
      title: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_delete_hint_text),
      note: languageStore.smartcatTranslationText((v) => v.confirmcancellation),
      confirmLabel: languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm),
    };
  }, [deleteConfirmState, languageStore]);
  return (
    <div className={styles.panel}>
      <div className={styles.panelHeader}>
        <div className={styles.panelTitleWrap}>
          <span className={styles.panelTitle}>
            {isTeamHistory
              ? teamHistoryTitleText
              : languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_history_text)}
          </span>
        </div>
        <button
          type="button"
          className={styles.panelCloseBtn}
          aria-label={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_sidebar_close)}
          onClick={() => setOpenAssetPopover?.(false)}
        >
          <IconFont name="crossBlack" fontSize={20} color="var(--content-primary, #fff)" />
        </button>
      </div>
      <div className={styles.panelBody}>
        <div className={styles.panelMain}>
          {
            <div className={styles.toolbar}>
              <div className={styles.tabs}>
                <div className={styles.dropdownWrap} ref={folderDropdownRef}>
                  <button
                    type="button"
                    className={cn(styles.tabBtn, { [styles.active]: folderDropdownOpen })}
                    onClick={() => {
                      closeOtherDropdowns('folder');
                      setFolderDropdownOpen((v) => !v);
                    }}
                  >
                    <span className={styles.tabBtnLabel}>{selectedFolderName}</span>
                    <IconFont name="chevronDownFill" fontSize={10} color="var(--content-buttonSecondary)" />
                  </button>
                  {folderDropdownOpen && (
                  <div
                    className={cn(styles.dropMenu, styles.dropMenuFolder)}
                    role="listbox"
                    aria-label={languageStore.smartcatTranslationText((v) => v.select_group)}
                  >
                    <button
                      type="button"
                      className={cn(styles.folderMenuItem, {
                        [styles.folderMenuItemSelected]: selectedFolderId == null,
                      })}
                      onClick={() => {
                        setSelectedFolderId(undefined);
                        setFolderDropdownOpen(false);
                      }}
                      role="option"
                      aria-selected={selectedFolderId == null}
                    >
                      <span
                        className={cn(styles.folderMenuLabel, {
                          [styles.folderMenuLabelSelected]: selectedFolderId == null,
                        })}
                      >
                        {languageStore.smartcatTranslationText((v) => v.roboneo_web_All)}
                      </span>
                      {selectedFolderId == null && (
                      <IconFont
                        name="checkV2Fill"
                        fontSize={10}
                        color="var(--content-primary)"
                        className={styles.menuCheck}
                      />
                      )}
                    </button>
                    {foldersOrdered.map((folder) => {
                      const idNum = Number(folder.id);
                      const selected = selectedFolderId === idNum;
                      return (
                        <div
                          key={folder.id}
                          className={cn(styles.folderMenuRow, { [styles.folderMenuRowSelected]: selected })}
                          role="presentation"
                        >
                          <button
                            type="button"
                            className={styles.folderMenuItemMain}
                            onClick={() => {
                              setSelectedFolderId(idNum);
                              setFolderDropdownOpen(false);
                            }}
                            role="option"
                            aria-selected={selected}
                          >
                            <span
                              className={cn(styles.folderMenuLabel, {
                                [styles.folderMenuLabelSelected]: selected,
                              })}
                            >
                              {folder.name}
                            </span>
                            {selected && (
                            <IconFont
                              name="checkV2Fill"
                              fontSize={10}
                              color="var(--content-primary)"
                              className={styles.menuCheck}
                            />
                            )}
                          </button>
                          <button
                            type="button"
                            className={styles.folderPinBtn}
                            aria-label={
                                  folder.isPinned
                                    ? languageStore.smartcatTranslationText((v) => v.asset_unpin_from_top)
                                    : languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_studio_set_front)
                                }
                            onClick={(e) => {
                              e.preventDefault();
                              e.stopPropagation();
                              handleToggleFolderPin(folder);
                            }}
                          >
                            <IconFont
                              name="pinFill"
                              fontSize={14}
                              color={
                                    folder.isPinned ? '#00E676' : 'var(--content-secondary, rgb(247 248 250 / 65%))'
                                  }
                            />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                  )}
                </div>

                <div className={styles.dropdownWrap} ref={taskDropdownRef}>
                  <button
                    type="button"
                    className={cn(styles.tabBtn, { [styles.active]: taskDropdownOpen })}
                    onClick={() => {
                      closeOtherDropdowns('task');
                      setTaskDropdownOpen((v) => !v);
                    }}
                  >
                    <span className={styles.tabBtnLabel}>{taskTypeLabel}</span>
                    <IconFont name="chevronDownFill" fontSize={10} color="var(--content-buttonSecondary)" />
                  </button>
                  {taskDropdownOpen && (
                    <div
                      className={styles.dropMenu}
                      role="listbox"
                      aria-label={languageStore.smartcatTranslationText((v) => v.workflow)}
                    >
                      {ASSET_LIST_TASK_TYPE_FILTER_OPTIONS.map((opt) => {
                        const selected = taskType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={cn(styles.iconMenuItem, { [styles.iconMenuItemSelected]: selected })}
                            onClick={() => {
                              setTaskType(opt.value);
                              setTaskDropdownOpen(false);
                            }}
                            role="option"
                            aria-selected={selected}
                          >
                            <div className={styles.iconMenuItemContent}>
                              <IconFont
                                name={opt.icon}
                                fontSize={16}
                                color={selected ? 'var(--content-primary)' : 'var(--content-secondary)'}
                              />
                              <span
                                className={cn(styles.iconMenuLabel, {
                                  [styles.iconMenuLabelSelected]: selected,
                                })}
                              >
                                {getTaskTypeFilterLabel(opt.value)}
                              </span>
                            </div>
                            {selected && (
                              <IconFont
                                name="checkV2Fill"
                                fontSize={10}
                                color="var(--content-primary)"
                                className={styles.menuCheck}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <div className={styles.dropdownWrap} ref={materialDropdownRef}>
                  <button
                    type="button"
                    className={cn(styles.tabBtn, { [styles.active]: materialDropdownOpen })}
                    onClick={() => {
                      closeOtherDropdowns('material');
                      setMaterialDropdownOpen((v) => !v);
                    }}
                  >
                    <span className={styles.tabBtnLabel}>{materialTypeLabel}</span>
                    <IconFont name="chevronDownFill" fontSize={10} color="var(--content-buttonSecondary)" />
                  </button>
                  {materialDropdownOpen && (
                    <div
                      className={styles.dropMenu}
                      role="listbox"
                      aria-label={languageStore.smartcatTranslationText((v) => v.asset_type_files)}
                    >
                      {materialOptionsVisible.map((opt) => {
                        const selected = materialType === opt.value;
                        return (
                          <button
                            key={opt.value}
                            type="button"
                            className={cn(styles.iconMenuItem, { [styles.iconMenuItemSelected]: selected })}
                            onClick={() => {
                              setMaterialType(opt.value);
                              setMaterialDropdownOpen(false);
                            }}
                            role="option"
                            aria-selected={selected}
                          >
                            <div className={styles.iconMenuItemContent}>
                              <IconFont
                                name={opt.icon}
                                fontSize={16}
                                color={selected ? 'var(--content-primary)' : 'var(--content-secondary)'}
                              />
                              <span
                                className={cn(styles.iconMenuLabel, {
                                  [styles.iconMenuLabelSelected]: selected,
                                })}
                              >
                                {opt.label}
                              </span>
                            </div>
                            {selected && (
                              <IconFont
                                name="checkV2Fill"
                                fontSize={10}
                                color="var(--content-primary)"
                                className={styles.menuCheck}
                              />
                            )}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>

                <button
                  type="button"
                  className={styles.tabFavorite}
                  aria-pressed={favoriteChecked}
                  onClick={() => {
                    setFavoriteChecked((v) => !v);
                  }}
                >
                  <span className={cn(styles.checkbox, { [styles.checked]: favoriteChecked })}>
                    {favoriteChecked && <IconFont name="checkBoxMark" fontSize={10} color="#fff" />}
                  </span>
                  <span>{languageStore.smartcatTranslationText((v) => v.collected_asset)}</span>
                </button>
              </div>
              <div className={styles.toolbarRight}>
                <button
                  type="button"
                  className={cn(styles.sortBtn, { [styles.sortBtnActive]: batchMode })}
                  onClick={toggleBatchMode}
                >
                  {batchMode ? (
                    <IconFont
                      name="arrowRightBrackets"
                      fontSize={14}
                      color="var(--content-buttonSecondary)"
                      aria-hidden
                    />
                  ) : (
                    <IconFont
                      name="multipleChoiceBold"
                      fontSize={14}
                      color="var(--content-buttonSecondary)"
                      aria-hidden
                    />
                  )}
                  <span>
                    {batchMode
                      ? languageStore.smartcatTranslationText((v) => v.asset_quit_batch)
                      : languageStore.smartcatTranslationText((v) => v.batched_processing)}
                  </span>
                </button>
                {isTeamHistory && (
                  <div className={styles.dropdownWrap} ref={memberDropdownRef}>
                    <button
                      type="button"
                      className={cn(styles.sortBtn, { [styles.sortBtnActive]: memberDropdownOpen })}
                      onClick={() => {
                        closeOtherDropdowns('member');
                        setMemberDropdownOpen((open) => !open);
                      }}
                      aria-haspopup="listbox"
                      aria-expanded={memberDropdownOpen}
                    >
                      <span className={styles.teamMemberFilterLabel} title={teamMemberFilterLabel}>
                        {teamMemberFilterLabel}
                      </span>
                      <IconFont name="chevronDownFill" fontSize={10} color="var(--content-buttonSecondary)" />
                    </button>
                    {memberDropdownOpen && (
                      <div
                        className={cn(styles.dropMenu, styles.teamMemberDropMenu)}
                        role="listbox"
                        aria-label={teamMembersText}
                      >
                        <div className={styles.teamMemberMenuTitle}>
                          {teamMembersText}
                        </div>
                        {teamMembersLoading && (
                          <div className={styles.teamMemberMenuState} role="status">
                            {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading)}
                          </div>
                        )}
                        {!teamMembersLoading && teamMembersLoadFailed && (
                          <div className={styles.teamMemberMenuState} role="status">
                            {languageStore.smartcatTranslationText((v) => v.roboneo_web_loading_failed)}
                          </div>
                        )}
                        {!teamMembersLoading && !teamMembersLoadFailed && teamMembers.length === 0 && (
                          <div className={styles.teamMemberMenuState} role="status">
                            {noTeamMembersText}
                          </div>
                        )}
                        {!teamMembersLoading && !teamMembersLoadFailed && teamMembers.map((member) => {
                          const memberUid = String(member.uid);
                          const selected = selectedCreatedByUid === memberUid;
                          const memberLabel = getTeamMemberLabel(member);
                          return (
                            <button
                              key={memberUid}
                              type="button"
                              className={cn(styles.teamMemberMenuItem, {
                                [styles.teamMemberMenuItemSelected]: selected,
                              })}
                              onClick={() => handleTeamMemberSelect(member)}
                              role="option"
                              aria-selected={selected}
                              title={memberLabel}
                            >
                              <span className={styles.teamMemberMenuItemMain}>
                                <TeamMemberAvatar member={member} />
                                <span className={styles.teamMemberName}>{memberLabel}</span>
                              </span>
                              {selected && (
                                <IconFont
                                  name="checkV2Fill"
                                  fontSize={10}
                                  color="var(--content-primary)"
                                  className={styles.teamMemberMenuCheck}
                                />
                              )}
                            </button>
                          );
                        })}
                      </div>
                    )}
                  </div>
                )}
                <div className={styles.dropdownWrap} ref={viewModeDropdownRef}>
                  {/* <button
        type="button"
        className={cn(styles.sortBtn, { [styles.sortBtnActive]: viewModeDropdownOpen })}
        onClick={() => {
          closeOtherDropdowns('viewMode');
          setViewModeDropdownOpen((v) => !v);
        }}
      > */}
                  {/* 房间视图 DOM 已注释：原为 listGroupMode === 'room' ? '房间视图' : '时间视图' */}
                  {/* <span>{languageStore.smartcatTranslationText((v) => v.roboneo_web_Ti_View)}</span>
        <IconFont name="chevronDownFill" fontSize={10} color="var(--content-buttonSecondary)" /> */}
                  {/* </button> */}
                  {viewModeDropdownOpen && (
                    <div
                      className={cn(styles.dropMenu, styles.viewModeDropMenu)}
                      role="listbox"
                      aria-label="选择列表视图"
                    >
                      {/* 房间视图 DOM 已注释
            <button
              type="button"
              className={cn(styles.sortOrderItem, { [styles.sortOrderItemActive]: listGroupMode === 'room' })}
              onClick={() => {
                setListGroupMode('room');
                setViewModeDropdownOpen(false);
              }}
              role="option"
              aria-selected={listGroupMode === 'room'}
            >
              <span className={styles.sortOrderItemLeft}>
                <span>房间视图</span>
              </span>
              {listGroupMode === 'room' && (
                <IconFont name="checkV2Fill" fontSize={10} color="var(--content-primary)" className={styles.sortOrderCheck} />
              )}
            </button>
            */}
                      <button
                        type="button"
                        className={cn(styles.sortOrderItem, { [styles.sortOrderItemActive]: listGroupMode === 'time' })}
                        onClick={() => {
                          setListGroupMode('time');
                          setViewModeDropdownOpen(false);
                        }}
                        role="option"
                        aria-selected={listGroupMode === 'time'}
                      >
                        <span className={styles.sortOrderItemLeft}>
                          <span>{languageStore.smartcatTranslationText((v) => v.roboneo_web_Ti_View)}</span>
                        </span>
                        {listGroupMode === 'time' && (
                          <IconFont
                            name="checkV2Fill"
                            fontSize={10}
                            color="var(--content-primary)"
                            className={styles.sortOrderCheck}
                          />
                        )}
                      </button>
                    </div>
                  )}
                </div>
                <button type="button" className={styles.sortBtn} onClick={toggleSortOrder}>
                  <IconFont
                    name={sortOrder === 'asc' ? 'sortUpBold' : 'sortDownBold'}
                    fontSize={16}
                    color="var(--content-buttonSecondary)"
                  />
                  <span>
                    {sortOrder === 'asc'
                      ? languageStore.smartcatTranslationText((v) => v.ascend)
                      : languageStore.smartcatTranslationText((v) => v.descend)}
                  </span>
                </button>
              </div>
            </div>
          }

          <div
            ref={scrollContentRef}
            className={styles.scrollContent}
            onScroll={handleAssetListScroll}
          >
            {loading && (
              <div className={styles.loadingWrap}>
                {languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_loading1)}
              </div>
            )}
            {!loading && visibleItemsOrdered.length === 0 && (
              <div className={styles.listEmptyWrap} role="status">
                <p className={styles.listEmptyText}>{languageStore.smartcatTranslationText((v) => v.empty)}</p>
                <div className={styles.listEmptyActions}>
                  <button
                    type="button"
                    className={styles.listEmptyActionBtn}
                    disabled={creatingEmptyCanvas}
                    onClick={handleCreateEmptyCanvas}
                  >
                    <IconFont name="newCanva" fontSize={17} />
                    {languageStore.smartcatTranslationText((v) => v.roboneo_web_create_canvas)}
                  </button>
                </div>
              </div>
            )}
            {!loading
              && visibleItemsOrdered.length > 0
              && groups.map((group) => (
                <div key={group.id} className={styles.group}>
                  <div className={styles.groupHeader}>
                    <span className={styles.groupName}>{group.name}</span>
                    <span className={styles.groupDate}>
                      {languageStore.smartcatTranslationText((v) => v.last)}
                      ：
                      {group.lastModified}
                    </span>
                  </div>
                  <div className={styles.masonryGrid}>
                    {masonryColumns(group.items)
                      .filter((col) => col.length > 0)
                      .map((column) => (
                        <div key={`${group.id}-col-${column[0]!.id}`} className={styles.masonryColumn}>
                          {column.map((item) => {
                            const isCardSelected = selectedIds.has(item.id);
                            const batchIneligible = batchMode
                              && limitBuiltInBatchMedia
                              && !isBuiltInModalBatchSelectableMaterialType(insertTarget, item.materialType);
                            const batchSelectionLimitDisabled = batchMode
                              && !isCardSelected
                              && !canToggleBatchAssetSelection(
                                selectedIds,
                                item.id,
                                subjectPickerMaxSelectableCount,
                              );
                            const canClickPreviewAsset = disableAssetItemClick && isAssetCardClickPreviewable(item);
                            const showInsertHoverBadge = !batchMode
                              && (Boolean(onAssetItemClick)
                                || shouldShowAssetInsertHoverBadge(insertTarget, item.materialType));
                            let cardAriaLabel = languageStore.smartcatTranslationText((v) => v.asset_library);
                            if (batchMode) {
                              if (batchIneligible) {
                                cardAriaLabel = insertTarget === 'workflow'
                                  ? languageStore.smartcatTranslationText(
                                    (v) => v.assets_cannot_be_opened_on_in_workflow,
                                  )
                                  : languageStore.smartcatTranslationText((v) => v.assets_cannot_be_opened_in_canvas);
                              } else {
                                cardAriaLabel = languageStore.smartcatTranslationText((v) => v.asset_unselect);
                              }
                            } else if (canClickPreviewAsset) {
                              cardAriaLabel = languageStore.smartcatTranslationText(
                                (v) => v.RoboNeo_Web_studio_card_preview,
                              );
                            } else if (onAssetItemClick && !disableAssetItemClick) {
                              cardAriaLabel = languageStore.smartcatTranslationText(
                                (v) => v.RoboNeo_Web_studio_tool_select,
                              );
                            } else if (shouldShowAssetInsertHoverBadge(insertTarget, item.materialType)) {
                              cardAriaLabel = insertHint;
                            }
                            const canvasDragEnabled = insertTarget === 'canvas'
                              && !batchMode
                              && !onAssetItemClick
                              && isCanvasInsertableMaterialType(item.materialType)
                              && !batchIneligible;
                            const cardRootStyle: React.CSSProperties = item.materialType === 'audio'
                              ? {
                                aspectRatio: 1,
                                width: ASSET_AUDIO_CARD_DISPLAY_PX,
                                maxWidth: '100%',
                                alignSelf: 'center',
                              }
                              : { aspectRatio: item.aspectRatio };
                            const useCompactHistoryActions = item.aspectRatio
                              > ASSET_MODAL_MASONRY_CARD_WIDTH / HISTORY_ASSET_STACKED_ACTIONS_MIN_HEIGHT_PX;
                            const assetLibraryCardClickable = batchMode || canClickPreviewAsset || !disableAssetItemClick;
                            return (
                              <AssetModalItemRoot
                                key={isTeamHistory ? `${taskType}:${item.id}` : item.id}
                                item={item}
                                canvasDragEnabled={canvasDragEnabled}
                                className={cn(styles.assetItem, {
                                  [styles.assetItemBatch]: batchMode,
                                  [styles.assetItemBatchIneligible]: batchIneligible,
                                  [styles.assetBatchSelectionLimitDisabled]: batchSelectionLimitDisabled,
                                  [styles.assetItemAudio]: item.materialType === 'audio',
                                })}
                                style={cardRootStyle}
                                role={assetLibraryCardClickable ? 'button' : undefined}
                                tabIndex={assetLibraryCardClickable ? 0 : -1}
                                aria-label={cardAriaLabel}
                                aria-pressed={batchMode && !batchIneligible ? isCardSelected : undefined}
                                aria-disabled={batchIneligible || batchSelectionLimitDisabled || undefined}
                                onExposed={trackCurrentTeamHistoryCardShow}
                                onClick={() => {
                                  trackCurrentTeamHistoryCardClick(item);
                                  if (batchMode) handleBatchItemToggle(item);
                                  else if (canClickPreviewAsset) handleAssetCardPreview(item);
                                  else if (!disableAssetItemClick) handleAddToCanvas(item);
                                }}
                                onKeyDown={(e) => {
                                  if (e.key === 'Enter') {
                                    trackCurrentTeamHistoryCardClick(item);
                                    if (batchMode) handleBatchItemToggle(item);
                                    else if (canClickPreviewAsset) handleAssetCardPreview(item);
                                    else if (!disableAssetItemClick) handleAddToCanvas(item);
                                  }
                                }}
                              >
                                {isDocumentCardLayout(item.cardVariant, item.materialType) ? (
                                  <AssetDocumentCardBody item={item} />
                                ) : (
                                  <>
                                    <div className={styles.assetCover}>
                                      <AssetModalMediaCover item={item} coverVoiceArtSrc={voiceArtSrc} />
                                    </div>
                                    <AssetMediaExtensionBadge item={item} />
                                  </>
                                )}
                                {!batchMode && (
                                  <div
                                    className={cn(styles.historyAssetHoverActions, {
                                      [styles.historyAssetHoverActionsCompact]: useCompactHistoryActions,
                                    })}
                                  >
                                    <div
                                      className={styles.historyAssetTopActions}
                                      role="presentation"
                                      onClick={(e) => e.stopPropagation()}
                                      onPointerDown={(e) => e.stopPropagation()}
                                    >
                                      <button
                                        type="button"
                                        className={styles.historyAssetActionBtn}
                                        aria-label={languageStore.smartcatTranslationText((v) => v['delete-'])}
                                        disabled={!canDelete}
                                        title={!canDelete ? permissionDeniedText : undefined}
                                        onClick={(e) => {
                                          blurHoverActionButtonAfterMouseClick(e);
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openDeleteHistoryAssetConfirm(item);
                                        }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                        }}
                                      >
                                        <IconFont name="trashCanBold" fontSize={14} color="#fff" />
                                      </button>
                                      <button
                                        type="button"
                                        className={styles.historyAssetActionBtn}
                                        aria-label={languageStore.smartcatTranslationText((v) => v.roboneo_web_more)}
                                        onClick={(e) => {
                                          e.preventDefault();
                                          e.stopPropagation();
                                          openItemActionMenu(item, e.currentTarget);
                                        }}
                                        onPointerDown={(e) => e.stopPropagation()}
                                        onKeyDown={(e) => {
                                          if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                        }}
                                      >
                                        <IconFont name="ellipsisFill" fontSize={14} color="#fff" />
                                      </button>
                                    </div>
                                    <button
                                      type="button"
                                      className={styles.historyAssetFavoriteBtn}
                                      aria-label={
                                        item.isFavorite
                                          ? languageStore.smartcatTranslationText((v) => v.collected_asset)
                                          : languageStore.smartcatTranslationText((v) => v.collect)
                                      }
                                      aria-pressed={Boolean(item.isFavorite)}
                                      onClick={(e) => {
                                        blurHoverActionButtonAfterMouseClick(e);
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleToggleHistoryAssetFavorite(item);
                                      }}
                                      onPointerDown={(e) => e.stopPropagation()}
                                      onKeyDown={(e) => {
                                        if (e.key === 'Enter' || e.key === ' ') e.stopPropagation();
                                      }}
                                    >
                                      <IconFont
                                        name={item.isFavorite ? 'bookmarkFill' : 'bookmarkBold'}
                                        fontSize={14}
                                        color={item.isFavorite ? '#34D26A' : '#fff'}
                                      />
                                    </button>
                                  </div>
                                )}
                                {batchMode && !batchIneligible && (
                                  <div
                                    className={cn(styles.checkbox, styles.assetSelectCheckbox, {
                                      [styles.checked]: isCardSelected,
                                      [styles.assetSelectCheckboxDisabled]: batchSelectionLimitDisabled,
                                    })}
                                    onClick={(e) => {
                                      e.stopPropagation();
                                      if (!batchSelectionLimitDisabled) handleBatchItemToggle(item);
                                    }}
                                    onKeyDown={(e) => {
                                      if (e.key === 'Enter' && !batchSelectionLimitDisabled) {
                                        e.stopPropagation();
                                        handleBatchItemToggle(item);
                                      }
                                    }}
                                    role="presentation"
                                  >
                                    {isCardSelected && <IconFont name="checkBoxMark" fontSize={10} color="#fff" />}
                                  </div>
                                )}
                                {showInsertHoverBadge && (
                                  <div className={styles.assetHoverHint}>
                                    <button
                                      type="button"
                                      className={styles.assetAddBadge}
                                      aria-label={insertHint}
                                      onClick={(e) => {
                                        blurHoverActionButtonAfterMouseClick(e);
                                        e.preventDefault();
                                        e.stopPropagation();
                                        handleAddToCanvas(item);
                                      }}
                                    >
                                      <IconFont name="uploadBold" fontSize={16} color="#fff" />
                                    </button>
                                  </div>
                                )}
                              </AssetModalItemRoot>
                            );
                          })}
                        </div>
                      ))}
                  </div>
                </div>
              ))}
            {!loading && loadingMore && (
              <div className={styles.loadingMoreWrap}>
                {languageStore.smartcatTranslationText((v) => v.asset_load_more)}
              </div>
            )}
          </div>

          {batchMode && (
            <div className={styles.batchBarOuter}>
              {isSubjectLibraryPicker && (
                <SubjectPickerBatchFooter
                  confirmLabel={batchPrimaryLabel}
                  maxSelectableCount={subjectPickerMaxSelectableCount ?? 0}
                  selectedCount={selectedIds.size}
                  variant="history"
                  onConfirm={handleBatchAdd}
                />
              )}
              <div
                className={styles.batchBar}
                role="toolbar"
                aria-label={languageStore.smartcatTranslationText((v) => v.batch_selection)}
                hidden={isSubjectLibraryPicker}
              >
                <button
                  type="button"
                  className={styles.batchSelectAllBtn}
                  onClick={toggleSelectAllVisible}
                  disabled={batchSelectScopeCount === 0}
                  hidden={hideBatchManagementActions}
                  aria-label={languageStore
                    .smartcatTranslationText((v) => v.select_all_8_selected_)
                    .replace('{N}', String(selectedIds.size))}
                >
                  <span
                    className={cn(styles.batchSelectAllCheck, {
                      [styles.batchSelectAllCheckEmpty]: selectedIds.size === 0,
                      [styles.batchSelectAllCheckIndeterminate]: someVisibleSelected,
                      [styles.batchSelectAllCheckAll]: allVisibleSelected,
                    })}
                  >
                    {someVisibleSelected && (
                      <span className={styles.batchSelectAllCheckIcon}>
                        <IconFont name="minusFill" fontSize={10} color="#fff" aria-hidden />
                      </span>
                    )}
                    {allVisibleSelected && (
                      <span className={styles.batchSelectAllCheckIcon}>
                        <IconFont name="checkBoxMark" fontSize={10} color="#fff" aria-hidden />
                      </span>
                    )}
                  </span>
                  <span className={styles.batchSelectAllLabel}>
                    {languageStore
                      .smartcatTranslationText((v) => v.select_all_8_selected_)
                      .replace('{N}', String(selectedIds.size))}
                  </span>
                </button>
                <div
                  className={cn(styles.batchBarRight, {
                    [styles.batchBarRightCompact]: hideBatchManagementActions,
                  })}
                >
                  <button
                    type="button"
                    className={styles.batchCancelSelectBtn}
                    onClick={clearBatchSelectionOnly}
                    disabled={selectedIds.size === 0}
                  >
                    {languageStore.smartcatTranslationText((v) => v.asset_unselect)}
                  </button>
                  <button
                    type="button"
                    className={styles.batchCancelSelectBtn}
                    onClick={handleBatchHistoryAssetFavorite}
                    disabled={selectedIds.size === 0}
                    hidden={hideBatchManagementActions}
                  >
                    {selectedHistoryAssetsAllFavorited
                      ? languageStore.smartcatTranslationText((v) => v.collected_asset)
                      : languageStore.smartcatTranslationText((v) => v.roboneo_web_favorite)}
                  </button>
                  <button
                    type="button"
                    className={styles.batchCancelSelectBtn}
                    title={!canDownload ? permissionDeniedText : undefined}
                    onClick={handleBatchHistoryAssetDownload}
                    disabled={selectedIds.size === 0 || !canDownload}
                    hidden={hideBatchManagementActions}
                  >
                    {languageStore.smartcatTranslationText((v) => v.download)}
                  </button>
                  <button
                    type="button"
                    className={styles.batchDangerBtn}
                    title={!canDelete ? permissionDeniedText : undefined}
                    onClick={openDeleteHistoryAssetBatchConfirm}
                    disabled={selectedIds.size === 0 || !canDelete}
                    hidden={hideBatchManagementActions}
                  >
                    {languageStore.smartcatTranslationText((v) => v['delete-'])}
                  </button>
                  {enableAddToAssetLibrary && (
                    <button
                      type="button"
                      className={styles.batchCancelSelectBtn}
                      onClick={openAddSelectedToLibraryDialog}
                      disabled={selectedIds.size === 0 || savingSelectedToLibrary}
                    >
                      {languageStore.smartcatTranslationText((v) => v.add_to_assets)}
                    </button>
                  )}
                  <button
                    type="button"
                    className={styles.batchConfirmBtn}
                    onClick={handleBatchAdd}
                    disabled={selectedIds.size === 0}
                  >
                    {batchPrimaryLabel}
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>
      {itemActionMenu
        && typeof document !== 'undefined'
        && createPortal(
          <div
            ref={itemActionMenuRef}
            className={cn(styles.mediaLibraryActionMenu, styles.mediaLibraryItemActionMenu)}
            style={{ left: itemActionMenu.left, top: itemActionMenu.top }}
            role="menu"
          >
            <button
              type="button"
              className={styles.mediaLibraryActionMenuItem}
              disabled={!canDownload}
              title={!canDownload ? permissionDeniedText : undefined}
              onClick={() => {
                handleAssetItemDownload(itemActionMenu.item);
              }}
            >
              <IconFont name="downloadBold" fontSize={16} color="currentColor" />
              <span>{languageStore.smartcatTranslationText((v) => v.download)}</span>
            </button>
          </div>,
          document.body,
        )}

      <AssetModalConfirmDialog
        visible={deleteConfirmState != null}
        title={deleteConfirmContent.title}
        note={deleteConfirmContent.note}
        confirmLabel={deleteConfirmContent.confirmLabel}
        onClose={() => setDeleteConfirmState(null)}
        onConfirm={handleConfirmAssetDelete}
      />

      <AssetModalMoveDialog
        visible={addToLibraryDialogOpen}
        folders={addToLibraryFoldersOrdered}
        targetFolderId={addToLibraryTargetFolderId}
        title={languageStore.smartcatTranslationText((v) => v.add_to_assets)}
        confirmLabel={languageStore.smartcatTranslationText((v) => v.RoboNeo_Web_toast_confirm)}
        onTargetFolderChange={setAddToLibraryTargetFolderId}
        onClose={() => {
          if (savingSelectedToLibrary) return;
          setAddToLibraryDialogOpen(false);
          setAddToLibraryTargetFolderId(undefined);
        }}
        onSubmit={handleSubmitAddSelectedToLibrary}
      />
    </div>
  );
}
export default HistoryModalContent;
